import { ActiveSearchTags } from '@/components/ActiveSearchTags';
import { CollapsableSearch } from '@/components/CollapsableSearch';
import { ErrorBoundary } from '@/components/Rollbar';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import type {
  AdminInvoiceReportsSearchFormData,
  AdminInvoiceReportsSearchQuery,
} from '@/features/admin_invoice_reports/types';
import { useApCounterpartyTransformer } from '@/hooks/invoice_reports/useApCounterpartyTransformer';
import { useApPayeeTransformer } from '@/hooks/invoice_reports/useApPayeeTransformer';
import { usePaymentStatusTransformer } from '@/hooks/invoice_reports/usePaymentStatusTransformer';
import { useShowSearchValue } from '@/hooks/invoice_reports/useShowSearchValue';
import { useTranslation } from '@/i18n';
import classnames from 'classnames/bind';
import { FC, memo, Suspense, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { SearchForm } from './components/SearchForm';
import {
  convertFormDataToApiQuery,
  getDefaultFormValues,
  useAdminInvoiceReportsFieldOrders,
  useApCounterpartyInitialData,
  useApPayeeInitialData,
  useConfigForm,
  useDeptInitialData,
  useOfficeMembersInitialData,
  usePaymentMethodTransformer,
  useSearchForm,
} from './hooks';
import styles from './Search.module.scss';

const cx = classnames.bind(styles);

interface SearchProps {
  defaultValues?: AdminInvoiceReportsSearchFormData;
  onSearch: (data: AdminInvoiceReportsSearchQuery) => void;
  searchQuery?: AdminInvoiceReportsSearchQuery;
  isLoading?: boolean;
}

export const InnerSearch: FC<SearchProps> = memo(
  ({ defaultValues, onSearch, searchQuery, isLoading }) => {
    const { t } = useTranslation();
    const form = useForm<AdminInvoiceReportsSearchFormData>({
      defaultValues: defaultValues ?? getDefaultFormValues(),
      mode: 'all',
    });

    const { handleReset } = useSearchForm({ form });

    const {
      labels,
      selectProps,
      inputProps,
      paymentStatusOptions,
      paymentMethodOptions,
      deptsExisted,
      reportDeptsSetting,
      deptDefaultOptions,
      officeInvoiceReportSettings,
    } = useConfigForm();

    const memberOptions = useOfficeMembersInitialData(
      defaultValues?.applicant_ids ?? []
    );
    const applicantDeptOptions = useDeptInitialData(
      defaultValues?.applicant_department_ids ?? []
    );
    const reportDeptInitialOptions = useDeptInitialData(
      defaultValues?.department_ids ?? []
    );
    const reportDeptOptions = useMemo(
      () => [
        ...(deptDefaultOptions || []),
        ...(reportDeptInitialOptions || []),
      ],
      [deptDefaultOptions, reportDeptInitialOptions]
    );

    const payeeOptions = useApPayeeInitialData(
      defaultValues?.ap_payee_ids ?? []
    );

    const counterpartyDefaultOptions = useApCounterpartyInitialData(
      defaultValues?.ap_counterparty_ids ?? []
    );

    const { FIELD_ORDERS } = useAdminInvoiceReportsFieldOrders();
    const paymentStatusTransformers = usePaymentStatusTransformer();
    const apPayeeTransformers = useApPayeeTransformer(
      payeeOptions,
      officeInvoiceReportSettings
    );
    const apCounterpartyTransformers = useApCounterpartyTransformer(
      counterpartyDefaultOptions
    );
    const paymentMethodTransformers =
      usePaymentMethodTransformer(paymentMethodOptions);
    const customTransformers = useMemo(
      () => ({
        ...paymentStatusTransformers,
        ...apPayeeTransformers,
        ...apCounterpartyTransformers,
        ...paymentMethodTransformers,
      }),
      [
        paymentStatusTransformers,
        apPayeeTransformers,
        apCounterpartyTransformers,
        paymentMethodTransformers,
      ]
    );

    // Keep URL/table sort on submit (same pattern as invoice_reports Search).
    const sortFields = defaultValues?.sort_fields;
    const sortOrders = defaultValues?.sort_orders;

    const onHandleSearch = useCallback(
      (data: AdminInvoiceReportsSearchFormData) => {
        // Strip page before calling onSearch so an empty form always goes through
        // the full-search path (not the page-only merge branch)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { page, ...queryWithoutPage } = convertFormDataToApiQuery(data);
        onSearch({
          ...queryWithoutPage,
          sort_fields: sortFields,
          sort_orders: sortOrders,
        });
      },
      [onSearch, sortFields, sortOrders]
    );

    const { searchValuesShow } = useShowSearchValue({
      searchQuery,
      fieldOrders: FIELD_ORDERS,
      payeeOptions,
      deptOptions: applicantDeptOptions,
      reportDeptOptions,
      memberOptions,
      excludeFields: [],
      customTransformers,
      alwaysShowFields: [],
    });

    const renderSearchForm = useCallback(
      () => (
        <SearchForm
          form={form}
          onSubmit={onHandleSearch}
          onReset={handleReset}
          isLoading={isLoading}
          labels={labels}
          selectProps={selectProps}
          inputProps={inputProps}
          paymentStatusOptions={paymentStatusOptions}
          paymentMethodOptions={paymentMethodOptions}
          deptsExisted={deptsExisted}
          reportDeptsSetting={reportDeptsSetting}
          memberOptions={memberOptions}
          applicantDeptOptions={applicantDeptOptions}
          reportDeptOptions={reportDeptOptions}
          appPayeeDefaultOptions={payeeOptions}
          officeInvoiceReportSettings={officeInvoiceReportSettings}
          counterpartyDefaultOptions={counterpartyDefaultOptions}
        />
      ),
      [
        form,
        onHandleSearch,
        handleReset,
        isLoading,
        labels,
        selectProps,
        inputProps,
        paymentStatusOptions,
        paymentMethodOptions,
        deptsExisted,
        reportDeptsSetting,
        memberOptions,
        applicantDeptOptions,
        reportDeptOptions,
        payeeOptions,
        officeInvoiceReportSettings,
        counterpartyDefaultOptions,
      ]
    );

    return (
      <div className={cx('search-container')}>
        <div className={cx('header')}>
          <CollapsableSearch
            initialOpen
            openTitle={t('admin_invoice_reports.search.collapse_title')}
            closeTitle={t('admin_invoice_reports.search.expand_title')}
            sectionTitleExpandedClassName={cx('section-title-expanded')}
          >
            {renderSearchForm}
          </CollapsableSearch>
        </div>
        <ActiveSearchTags activeSearchValues={searchValuesShow} />
      </div>
    );
  }
);
InnerSearch.displayName = 'InnerSearch';

const renderErrorFallback = () => null;

export const Search: FC<SearchProps> = memo(
  ({ defaultValues, onSearch, searchQuery, isLoading }) => (
    <Suspense
      fallback={<SuspenseLoading className={cx('search-container-loading')} />}
    >
      <ErrorBoundary fallback={renderErrorFallback}>
        <InnerSearch
          defaultValues={defaultValues}
          onSearch={onSearch}
          searchQuery={searchQuery}
          isLoading={isLoading}
        />
      </ErrorBoundary>
    </Suspense>
  )
);
Search.displayName = 'Search';
